import type { CellContext, ColumnDef } from '@tanstack/react-table';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { DataTable } from './components/DataTable';
import { EditableCell } from './components/EditableCell';
import { ExpandPanel, type ExpandSection } from './components/ExpandPanel';
import { NestedTable } from './components/NestedTable';
import type { EnvSchema } from './EnvSelector';
import { derivePk, derivePkColumns } from './lib/derive-pk';
import { refreshIntrospection } from './lib/enum-introspect';
import { RELATIONSHIPS } from './lib/relationships';
import { type ColumnType, getColumnType, getEditableColumns } from './lib/table-columns';
import { buildChildCacheKey, buildParentCacheKey, useFkExpand } from './lib/use-fk-expand';
import { callEdgeFunction } from './supabase.client';
import type { TableName } from './TableSidebar';
import { StageView } from './views/StageView';

type RowMap = Record<string, unknown>;

const VIEW_REGISTRY: Record<string, React.ComponentType<{ env: EnvSchema }>> = {
  stage: StageView,
};

interface ListResp {
  rows: RowMap[];
}

interface Props {
  env: EnvSchema;
  table: TableName;
}

interface RowState {
  /** Row identity in the rendered list. For new rows, a `__new:N` sentinel. */
  key: string;
  /** Original row from server (null for unsaved new rows). */
  original: RowMap | null;
  /** Mutable working copy used by the inline editor. */
  draft: RowMap;
  /** Whether this row is currently in edit mode. */
  editing: boolean;
  /** Whether this row was created locally and hasn't been saved yet. */
  isNew: boolean;
  /** In-flight save error (if any). */
  error?: string | null;
}

let newCounter = 0;

export function TableEditor({ env, table }: Props) {
  const Custom = VIEW_REGISTRY[table];
  if (Custom) return <Custom env={env} />;

  return <GenericEditor env={env} table={table} />;
}

function GenericEditor({ env, table }: Props) {
  const [rows, setRows] = useState<RowState[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // key currently saving/deleting

  const fk = useFkExpand(env);
  const rels = RELATIONSHIPS[table];
  const parentFKByColumn = useMemo(() => new Map(rels.parents.map((p) => [p.column, p])), [rels]);

  // Pre-fetched parent rows keyed by parent ref-table — used by EditableCell
  // to populate FkCombobox / FkMultiSelect options. Lazy: filled on first
  // edit-mode entry per table.
  const [fkOptions, setFkOptions] = useState<Record<string, RowMap[]>>({});

  const ensureFkOptions = useCallback(async () => {
    const parentTables = Array.from(new Set(rels.parents.map((p) => p.refTable)));
    const missing = parentTables.filter((t) => !fkOptions[t]);
    if (missing.length === 0) return;
    try {
      const results = await Promise.all(
        missing.map((t) =>
          callEdgeFunction<ListResp>('crud', {
            schema: env,
            table: t,
            op: 'list',
            limit: 1000,
          }).then((r) => [t, r.rows] as const)
        )
      );
      setFkOptions((prev) => ({ ...prev, ...Object.fromEntries(results) }));
    } catch (e) {
      // FK option prefetch is best-effort — never block editing on failure,
      // but log so an empty dropdown can be diagnosed from the browser console.
      console.error('[TableEditor] FK prefetch failed:', e);
    }
  }, [env, rels, fkOptions]);

  // Composite-PK columns are locked in edit mode. Single-PK tables only
  // lock when the user is editing an existing row (the column is part of
  // the natural key) — but for now we treat composite-only specially since
  // single-PK rows already work via the existing inline editor.
  const compositePkColumns = useMemo(() => {
    const sample = rows.find((r) => r.original)?.original;
    if (!sample) return new Set<string>();
    const pk = derivePk(sample, table);
    const keys = Object.keys(pk);
    return keys.length > 1 ? new Set(keys) : new Set<string>();
  }, [rows, table]);

  const reload = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await callEdgeFunction<ListResp>('crud', {
        schema: env,
        table,
        op: 'list',
        limit: 1000,
      });
      const next: RowState[] = r.rows.map((row) => ({
        key: rowKey(row, table),
        original: row,
        draft: { ...row },
        editing: false,
        isNew: false,
      }));
      setRows(next);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [env, table]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    void refreshIntrospection(env);
  }, [env]);

  // Derive editable columns from row sample or the static column map.
  const sample = rows.find((r) => r.original)?.original ?? rows.find((r) => r.isNew)?.draft;
  const cols = useMemo(() => getEditableColumns(table, sample ?? undefined), [table, sample]);

  const updateRow = useCallback((key: string, mutator: (r: RowState) => RowState) => {
    setRows((prev) => prev.map((r) => (r.key === key ? mutator(r) : r)));
  }, []);

  const startEdit = useCallback(
    (key: string) => {
      // Editing a row clears any FK expansion for that row — they'd render
      // stale data once the user saves edits.
      fk.collapseRow(key);
      // Best-effort prefetch of parent rows so FkCombobox / FkMultiSelect
      // have options ready when the inline editor renders.
      void ensureFkOptions();
      updateRow(key, (r) => ({
        ...r,
        editing: true,
        draft: { ...(r.original ?? r.draft) },
        error: null,
      }));
    },
    [updateRow, fk, ensureFkOptions]
  );

  const cancelEdit = useCallback((key: string) => {
    setRows((prev) => {
      // If new row, drop it entirely. Else revert draft → original.
      const target = prev.find((r) => r.key === key);
      if (!target) return prev;
      if (target.isNew) return prev.filter((r) => r.key !== key);
      return prev.map((r) =>
        r.key === key ? { ...r, editing: false, draft: { ...(r.original ?? {}) }, error: null } : r
      );
    });
  }, []);

  const setDraftValue = useCallback(
    (key: string, col: string, value: unknown) => {
      updateRow(key, (r) => ({ ...r, draft: { ...r.draft, [col]: value } }));
    },
    [updateRow]
  );

  // saveRow / deleteRow need access to the latest `rows` array; we read
  // through a ref so the callbacks themselves stay referentially stable
  // (otherwise `tableColumns` rebuilds on every keystroke).
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const saveRow = useCallback(
    async (key: string) => {
      const target = rowsRef.current.find((r) => r.key === key);
      if (!target) return;
      setBusy(key);
      try {
        if (target.isNew) {
          // Strip empty-string / undefined columns so the EF doesn't try to write them.
          const values = stripUndefined(target.draft);
          await callEdgeFunction('crud', {
            schema: env,
            table,
            op: 'insert',
            values,
          });
        } else {
          const original = target.original ?? {};
          const diff = diffValues(original, target.draft);
          if (Object.keys(diff).length === 0) {
            updateRow(key, (r) => ({ ...r, editing: false, error: null }));
            setBusy(null);
            return;
          }
          const pk = derivePk(original, table);
          await callEdgeFunction('crud', {
            schema: env,
            table,
            op: 'update',
            pk,
            values: diff,
          });
        }
        await reload();
      } catch (e) {
        updateRow(key, (r) => ({ ...r, error: (e as Error).message }));
      } finally {
        setBusy(null);
      }
    },
    [env, table, updateRow, reload]
  );

  const deleteRow = useCallback(
    async (key: string) => {
      const target = rowsRef.current.find((r) => r.key === key);
      if (!target?.original) return;
      const pk = derivePk(target.original, table);
      if (!confirm(`Delete ${JSON.stringify(pk)}?`)) return;
      setBusy(key);
      try {
        await callEdgeFunction('crud', {
          schema: env,
          table,
          op: 'delete',
          pk,
        });
        await reload();
      } catch (e) {
        updateRow(key, (r) => ({ ...r, error: (e as Error).message }));
      } finally {
        setBusy(null);
      }
    },
    [env, table, updateRow, reload]
  );

  const colsRef = useRef(cols);
  colsRef.current = cols;

  const addNewRow = useCallback(() => {
    newCounter += 1;
    const blank: RowMap = {};
    for (const c of colsRef.current) blank[c] = null;
    void ensureFkOptions();
    setRows((prev) => [
      {
        key: `__new:${newCounter}`,
        original: null,
        draft: blank,
        editing: true,
        isNew: true,
      },
      ...prev,
    ]);
  }, [ensureFkOptions]);

  const rowStateKey = useCallback((r: RowState) => r.key, []);

  const tableColumns = useMemo<ColumnDef<RowState>[]>(() => {
    const expandCol: ColumnDef<RowState> = {
      id: '__expand',
      header: '',
      enableSorting: false,
      size: 32,
      cell: ({ row }) => {
        const r = row.original;
        // Hide the expand affordance for unsaved-new rows or rows being edited:
        // their FK relationships aren't meaningful until persisted.
        if (r.isNew || r.editing) return null;
        if (rels.children.length === 0 && rels.parents.length === 0) return null;
        const isOpen = fk.expanded.has(r.key);
        return (
          <button
            type="button"
            onClick={() => fk.toggleRowExpanded(r.key)}
            className="hover:bg-muted rounded p-0.5"
          >
            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        );
      },
    };

    const dataCols: ColumnDef<RowState>[] = cols.map((col) => {
      const parentFK = parentFKByColumn.get(col);
      return {
        id: col,
        accessorFn: (r) => r.draft[col],
        header: col,
        sortingFn: (a, b) => {
          const av = a.original.draft[col];
          const bv = b.original.draft[col];
          if (av == null && bv == null) return 0;
          if (av == null) return -1;
          if (bv == null) return 1;
          if (typeof av === 'number' && typeof bv === 'number') return av - bv;
          return String(av).localeCompare(String(bv));
        },
        cell: (ctx: CellContext<RowState, unknown>) => {
          const r = ctx.row.original;
          const type: ColumnType = getColumnType(table, col);
          const value = r.draft[col];

          // Edit mode → existing inline editor takes over. Composite-PK
          // columns and FK columns get specialised renderers via the
          // EditableCell branches.
          if (r.editing) {
            return (
              <EditableCell
                type={type}
                value={value}
                onChange={(v) => setDraftValue(r.key, col, v)}
                editing
                ariaLabel={`${table} ${col}`}
                table={table}
                column={col}
                fkOptions={fkOptions}
                isCompositePkColumn={!r.isNew && compositePkColumns.has(col)}
                env={env}
              />
            );
          }

          // Read mode → render the cell value, with an inline ▶ on FK columns
          // that have a non-empty value.
          const hasFkValue = parentFK
            ? parentFK.isArray
              ? Array.isArray(value) && (value as unknown[]).length > 0
              : value != null
            : false;

          if (parentFK && hasFkValue) {
            const cacheKey = buildParentCacheKey(r.key, parentFK.column);
            const isOpen = fk.parentExpanded.has(cacheKey);
            return (
              <span className="inline-flex items-center gap-1">
                <EditableCell
                  type={type}
                  value={value}
                  onChange={() => {
                    /* read-only — onChange is required by EditableCell */
                  }}
                  editing={false}
                  ariaLabel={`${table} ${col}`}
                  env={env}
                />
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={async () => {
                    if (!isOpen) await fk.loadParent(r.key, parentFK, value);
                    fk.toggleParentExpanded(cacheKey);
                    fk.ensureRowExpanded(r.key);
                  }}
                >
                  {isOpen ? (
                    <ChevronDown className="h-3 w-3 inline" />
                  ) : (
                    <ChevronRight className="h-3 w-3 inline" />
                  )}
                </button>
              </span>
            );
          }

          return (
            <EditableCell
              type={type}
              value={value}
              onChange={(v) => setDraftValue(r.key, col, v)}
              editing={false}
              ariaLabel={`${table} ${col}`}
              env={env}
            />
          );
        },
      };
    });

    const actionsCol: ColumnDef<RowState> = {
      id: '__actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => {
        const r = row.original;
        const inFlight = busy === r.key;
        if (r.editing) {
          return (
            <div className="flex flex-col gap-1">
              <div className="flex gap-1">
                <Button type="button" size="xs" onClick={() => saveRow(r.key)} disabled={inFlight}>
                  {inFlight ? 'Saving…' : 'Save'}
                </Button>
                <Button
                  type="button"
                  size="xs"
                  variant="outline"
                  onClick={() => cancelEdit(r.key)}
                  disabled={inFlight}
                >
                  Cancel
                </Button>
              </div>
              {r.error && (
                <span className="text-[10px] text-rose-600 dark:text-rose-400">{r.error}</span>
              )}
            </div>
          );
        }
        return (
          <div className="flex gap-1">
            <Button
              type="button"
              size="xs"
              variant="outline"
              onClick={() => startEdit(r.key)}
              disabled={inFlight}
            >
              Edit
            </Button>
            <Button
              type="button"
              size="xs"
              variant="destructive"
              onClick={() => deleteRow(r.key)}
              disabled={inFlight}
            >
              Del
            </Button>
          </div>
        );
      },
    };
    return [expandCol, ...dataCols, actionsCol];
  }, [
    cols,
    table,
    busy,
    setDraftValue,
    startEdit,
    cancelEdit,
    saveRow,
    deleteRow,
    rels,
    fk,
    parentFKByColumn,
    fkOptions,
    compositePkColumns,
    env,
  ]);

  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading {table}…</div>;
  }
  if (err) {
    return (
      <div className="flex items-center gap-2 text-sm text-rose-600 dark:text-rose-400">
        Error: {err}
        <Button type="button" size="sm" variant="outline" onClick={reload}>
          Retry
        </Button>
      </div>
    );
  }

  const pkCols = derivePkColumns(table);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-base font-medium text-foreground">
          {env} / <span className="font-mono">{table}</span>{' '}
          <span className="text-xs text-muted-foreground">
            ({rows.length} rows · pk: {pkCols.join(', ')})
          </span>
        </h2>
      </div>
      {table === 'payment_event' && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          Only one row should have <span className="font-mono">active</span> = true at a time —
          switching a new row on does not auto-disable the others.
        </p>
      )}
      <DataTable<RowState>
        data={rows}
        columns={tableColumns}
        toolbar={
          <Button type="button" size="sm" onClick={addNewRow}>
            + New row
          </Button>
        }
        filterPlaceholder={`Filter ${table}…`}
        expandedRows={fk.expanded}
        rowKey={rowStateKey}
        renderSubRow={(rowState) => {
          // Editing or unsaved-new rows never render a sub-row, even if the
          // expand state still contains the key from before the edit.
          if (rowState.editing || rowState.isNew) return null;
          const original = rowState.original;
          if (!original) return null;
          const rowK = rowState.key;

          const parentExpansions = rels.parents
            .filter((p) => fk.parentExpanded.has(buildParentCacheKey(rowK, p.column)))
            .map((p) => {
              const cacheKey = buildParentCacheKey(rowK, p.column);
              const parentRows = fk.parentCache[cacheKey] ?? [];
              const isLoading = fk.parentLoading.has(cacheKey);
              return (
                <div
                  key={cacheKey}
                  className="my-1 border-l-2 border-blue-300 dark:border-blue-700 pl-2"
                >
                  <div className="text-xs text-muted-foreground mb-1">
                    parent: {p.refTable} ({p.column})
                  </div>
                  {isLoading ? (
                    <div className="text-xs italic">loading…</div>
                  ) : parentRows.length === 0 ? (
                    <div className="text-xs italic">not found</div>
                  ) : (
                    <NestedTable env={env} table={p.refTable} rows={parentRows} />
                  )}
                </div>
              );
            });

          const childSections: ExpandSection[] = rels.children.map((c) => {
            const k = buildChildCacheKey(rowK, c.table);
            const cached = fk.childCache[k];
            return {
              key: k,
              label: `→ ${c.label} (${c.table})`,
              count: cached?.length,
              loaded: !!cached,
              loading: fk.loadingChildren.has(k),
              load: () => fk.loadChildren(rowK, original, c),
              render: () => <NestedTable env={env} table={c.table} rows={cached ?? []} />,
              refresh: () => fk.refreshChildren(rowK, original, c),
            };
          });

          return (
            <>
              {parentExpansions}
              <ExpandPanel sections={childSections} />
            </>
          );
        }}
      />
    </div>
  );
}

function rowKey(row: RowMap, table: string): string {
  const pk = derivePk(row, table);
  return JSON.stringify(pk);
}

function diffValues(orig: RowMap, draft: RowMap): RowMap {
  const out: RowMap = {};
  const keys = new Set([...Object.keys(orig), ...Object.keys(draft)]);
  for (const k of keys) {
    if (k === 'updated_at') continue;
    const a = orig[k];
    const b = draft[k];
    if (!sameValue(a, b)) out[k] = b;
  }
  return out;
}

function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (typeof a === 'object' && typeof b === 'object') {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return false;
}

function stripUndefined(row: RowMap): RowMap {
  const out: RowMap = {};
  for (const [k, v] of Object.entries(row)) {
    if (v === undefined) continue;
    out[k] = v;
  }
  return out;
}
